import { useEffect, useState } from 'react'
import MaterialIcon from './MaterialIcon'
import { useHoldStepper } from '../../hooks/useHoldStepper'
import { KILO } from '../../constants/KILO_THEME'

type CenteredMetricStepperProps = {
  label: string
  value: number | null
  unit: string
  icon: string
  min: number
  max: number
  step?: number
  onSave: (value: number) => Promise<void>
}

const CenteredMetricStepper = ({
  label,
  value,
  unit,
  icon,
  min,
  max,
  step = 1,
  onSave,
}: CenteredMetricStepperProps) => {
  const resolved = value ?? min
  const [draft, setDraft] = useState(resolved)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(value ?? min)
  }, [value, min])

  const { activeZone, atLimit, bindDecrement, bindIncrement, pct } = useHoldStepper({
    value: draft,
    min,
    max,
    step,
    onChange: setDraft,
  })

  const dirty = draft !== resolved
  const display = draft % 1 === 0 ? String(draft) : draft.toFixed(1)

  const handleSave = async () => {
    setSaving(true)
    await onSave(draft)
    setSaving(false)
  }

  return (
    <div className="flex min-h-[min(70vh,32rem)] flex-col">
      <div className="flex flex-1 flex-col items-center justify-center px-2">
        <MaterialIcon name={icon} size="xl" className="mb-8 text-kilo-accent" />
        <p className={`mb-6 text-xs font-bold uppercase tracking-widest ${KILO.text.soft}`}>{label}</p>

        <div className="flex w-full max-w-sm items-center justify-between gap-4">
          <button
            type="button"
            aria-label={`Decrease ${label}`}
            className={`flex h-16 w-16 shrink-0 touch-none select-none items-center justify-center rounded-full border text-3xl font-light transition-colors ${
              activeZone === 'dec'
                ? 'border-kilo-accent/50 bg-kilo-accent/10 text-kilo-accent'
                : atLimit === 'min'
                  ? 'border-kilo-danger/40 bg-kilo-danger/10 text-kilo-danger'
                  : 'border-kilo-border bg-kilo-card text-kilo-soft hover:border-kilo-accent/30'
            }`}
            {...bindDecrement()}
          >
            −
          </button>

          <div className="min-w-0 flex-1 text-center">
            <div className="flex items-baseline justify-center gap-1.5">
              <span
                className={`text-5xl font-bold leading-none transition-colors ${
                  atLimit ? 'text-kilo-danger' : KILO.text.primary
                }`}
              >
                {display}
              </span>
              {unit ? <span className={`text-lg ${KILO.text.muted}`}>{unit}</span> : null}
            </div>
          </div>

          <button
            type="button"
            aria-label={`Increase ${label}`}
            className={`flex h-16 w-16 shrink-0 touch-none select-none items-center justify-center rounded-full border text-3xl font-light transition-colors ${
              activeZone === 'inc'
                ? 'border-kilo-accent/50 bg-kilo-accent/10 text-kilo-accent'
                : atLimit === 'max'
                  ? 'border-kilo-danger/40 bg-kilo-danger/10 text-kilo-danger'
                  : 'border-kilo-border bg-kilo-card text-kilo-soft hover:border-kilo-accent/30'
            }`}
            {...bindIncrement()}
          >
            +
          </button>
        </div>

        <div className="mt-10 h-1 w-full max-w-xs overflow-hidden rounded-full bg-kilo-nested">
          <div
            className="h-full rounded-full bg-kilo-accent transition-all duration-150"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {dirty ? (
        <div className="flex shrink-0 gap-2 border-t border-kilo-border pt-4">
          <button
            type="button"
            onClick={() => setDraft(resolved)}
            className={`flex-1 rounded-xl border border-kilo-border py-3 text-sm font-medium ${KILO.text.muted} transition-colors hover:border-kilo-accent/30`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="flex-1 rounded-xl bg-kilo-accent py-3 text-sm font-bold text-black transition-opacity disabled:opacity-50"
          >
            {saving ? '…' : 'Save'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default CenteredMetricStepper
